// Same order as backend `_CABIN_TIER` (routes.py). A peak is not a same-cabin
// was-price when a higher-tier cabin on that collection day was cheaper.

export const CABIN_TIER = {
  interior: 0,
  inside: 0,
  porthole: 1,
  'ocean view': 2,
  oceanview: 2,
  outside: 2,
  'outside view': 3,
  lanai: 3,
  balcony: 4,
  veranda: 4,
  'aqua class': 5,
  aquaclass: 5,
  concierge: 5,
  'mini suite': 5,
  'mini-suite': 5,
  minisuite: 5,
  suite: 6,
  suites: 6,
  'vista suite': 6,
  'signature suite': 7,
  'sky suite': 7,
  deluxe: 8,
  'neptune suite': 8,
};

export function cabinTier(name) {
  if (!name) return null;
  const key = String(name).trim().toLowerCase().replace(/\s+/g, ' ');
  if (!key || key === 'unknown') return null;
  return Object.prototype.hasOwnProperty.call(CABIN_TIER, key) ? CABIN_TIER[key] : null;
}

export function peakIsCoherent(history, cabinType, peak) {
  const tier = cabinTier(cabinType);
  if (tier == null || !Number.isFinite(peak)) return true;
  const peakDays = new Set();
  const rows = [];
  for (const point of history) {
    const price = Number(point.price);
    if (!Number.isFinite(price)) continue;
    const day = String(point.timestamp || '').slice(0, 10);
    const cabin = point.cabin_type || 'Unknown';
    rows.push({ day, cabin, price });
    if (cabin === cabinType && Math.abs(price - peak) < 0.01) peakDays.add(day);
  }
  if (!peakDays.size) return true;
  // CP-268 follow-up (same as backend _same_cabin_peak_is_trustworthy): a
  // fare at the sail's latest collection, where a higher-tier cabin was
  // recorded and none was cheaper, is the current coherent fare. Older
  // higher-cabin offer stamps must not hide it (15266 Interior $2,869).
  const latestDay = rows.reduce((max, row) => (row.day > max ? row.day : max), '');
  if (latestDay && peakDays.has(latestDay)) {
    const higher = rows.filter((row) => {
      if (row.day !== latestDay || row.cabin === cabinType) return false;
      const other = cabinTier(row.cabin);
      return other != null && other > tier;
    });
    if (higher.length && higher.every((row) => row.price + 0.01 >= peak)) return true;
  }
  for (const row of rows) {
    if (!peakDays.has(row.day) || row.cabin === cabinType) continue;
    const other = cabinTier(row.cabin);
    if (other != null && other > tier && row.price + 0.01 < peak) return false;
  }
  return true;
}

// Highest same-cabin fare that survives the guard. Drops each rejected peak
// and tries the next, so a swapped spike cannot become Highest.
export function coherentHigh(history, cabinType) {
  const rows = (history || []).filter(
    (point) => (point.cabin_type || 'Unknown') === cabinType
  );
  let prices = rows
    .map((point) => Number(point.price))
    .filter((price) => Number.isFinite(price));
  while (prices.length) {
    const peak = Math.max(...prices);
    if (peakIsCoherent(history, cabinType, peak)) return peak;
    prices = prices.filter((price) => Math.abs(price - peak) >= 0.01);
  }
  return null;
}

function sailKey(point) {
  return String(point.sail_date || '').slice(0, 10);
}

// Prices that stay once fares above the trustworthy high are removed.
// Same walk as the chart: a rejected peak is not part of the series, and
// a lower fare stays. Unflagged history keeps every price.
function pricesAtOrBelowTrustworthyHigh(cabinRows) {
  const flagged = cabinRows.some(
    (point) => typeof point.trustworthy_as_high === 'boolean'
  );
  if (!flagged) return cabinRows;
  return dropRejectedPeaks(cabinRows, (peak) => (
    cabinRows.some((point) => (
      point.trustworthy_as_high === true
      && Math.abs(Number(point.price) - peak) < 0.01
    ))
  ));
}

// Higher-cabin points that repeat a lower cabin's fare on this sail.
// Same-day: any strictly lower tier at the same price. Across the sail:
// a tier-0 cabin (Interior / Inside) with only one price left after the
// chart drops its spikes — the Interior row is sometimes missing on the
// contaminated day, and one Interior spike must not hide that flat
// lead-in. A dip at or below the trustworthy high still counts as a
// second fare. Tier-0 points are never stamps. Does not write history.
function copiedLowerTierStamps(sailRows) {
  const parsed = [];
  for (const point of sailRows) {
    const price = Number(point.price);
    if (!Number.isFinite(price)) continue;
    const cabin = point.cabin_type || 'Unknown';
    parsed.push({
      point,
      day: String(point.timestamp || '').slice(0, 10),
      cabin,
      tier: cabinTier(cabin),
      price,
    });
  }
  const byCabin = new Map();
  for (const row of parsed) {
    if (row.tier == null) continue;
    if (!byCabin.has(row.cabin)) byCabin.set(row.cabin, []);
    byCabin.get(row.cabin).push(row);
  }
  const flatTier0 = [];
  for (const rows of byCabin.values()) {
    if (rows[0].tier !== 0) continue;
    const kept = pricesAtOrBelowTrustworthyHigh(rows.map((row) => row.point));
    const keys = new Set(
      kept.map((point) => Math.round(Number(point.price) * 100) / 100)
    );
    if (keys.size === 1 && kept.length) flatTier0.push(Number(kept[0].price));
  }
  const byDay = new Map();
  for (const row of parsed) {
    if (!byDay.has(row.day)) byDay.set(row.day, []);
    byDay.get(row.day).push(row);
  }
  // CP-268 follow-up (same as backend): a higher cabin cheaper than the
  // tier-0 fare collected that same day, at a price tier-0 had on an
  // earlier day, repeats a superseded lead-in (15266 Ocean View $560 on
  // Oct 19-21 while Interior is $2,869).
  const tier0Seen = parsed.filter((row) => row.tier === 0 && row.day);
  const repeatsSupersededLeadIn = (row) => {
    if (!row.day) return false;
    const sameDay = tier0Seen.filter((seen) => seen.day === row.day);
    if (!sameDay.length || !sameDay.every((seen) => row.price + 0.01 < seen.price)) return false;
    return tier0Seen.some((seen) => (
      seen.day < row.day && Math.abs(seen.price - row.price) < 0.01
    ));
  };
  const copied = new Set();
  for (const row of parsed) {
    if (row.tier == null || row.tier === 0) continue;
    const matchesFlat = flatTier0.some((sample) => Math.abs(sample - row.price) < 0.01)
      || repeatsSupersededLeadIn(row);
    const matchesDay = (byDay.get(row.day) || []).some((other) => (
      other.cabin !== row.cabin
      && other.tier != null
      && other.tier < row.tier
      && Math.abs(other.price - row.price) < 0.01
    ));
    if (matchesFlat || matchesDay) copied.add(row.point);
  }
  return copied;
}

// Drop price levels the cabin-order guard rejected, highest first, and keep
// everything at or below the first level it accepts. A lower fare that is
// not itself a valid "high" (for example a dip day) stays on the line.
function dropRejectedPeaks(cabinRows, peakIsOk) {
  let remaining = cabinRows.filter((point) => Number.isFinite(Number(point.price)));
  while (remaining.length) {
    const peak = Math.max(...remaining.map((point) => Number(point.price)));
    if (peakIsOk(peak)) return remaining;
    remaining = remaining.filter((point) => Math.abs(Number(point.price) - peak) >= 0.01);
  }
  return [];
}

// Detail Price History points for one cabin. Drops a higher cabin's
// Interior-equal stamps, then the same peak guard as the Highest tile:
// a swapped or mislabeled spike is not plotted. Rows at or below the
// trustworthy high stay, so an honest series is unchanged. Does not write
// PriceHistory.
export function chartSeriesPoints(history, cabinType) {
  const all = Array.isArray(history) ? history : [];
  const rows = all.filter(
    (point) => (point.cabin_type || 'Unknown') === cabinType
  );
  if (!rows.length) return [];

  const kept = [];
  for (const sail of new Set(rows.map(sailKey))) {
    const sailRows = all.filter((point) => sailKey(point) === sail);
    const copied = copiedLowerTierStamps(sailRows);
    const cabinRows = sailRows.filter(
      (point) => (point.cabin_type || 'Unknown') === cabinType && !copied.has(point)
    );
    if (!cabinRows.length) continue;
    const flagged = cabinRows.some(
      (point) => typeof point.trustworthy_as_high === 'boolean'
    );
    if (flagged) {
      kept.push(...dropRejectedPeaks(cabinRows, (peak) => (
        cabinRows.some((point) => (
          point.trustworthy_as_high === true
          && Math.abs(Number(point.price) - peak) < 0.01
        ))
      )));
      continue;
    }
    const ceiling = coherentHigh(sailRows, cabinType);
    if (ceiling == null) continue;
    kept.push(...cabinRows.filter((point) => {
      const price = Number(point.price);
      return Number.isFinite(price) && price <= ceiling + 0.01;
    }));
  }
  return kept;
}

// CP-98: Detail cabin tiles in cheap -> premium order. pricesByType keys
// follow first appearance in the API rows, which is arbitrary. Sort by the
// tile's Lowest, then Current, then cabin tier, then name. Returns
// [cabinType, data] pairs; the input object is not changed.
export function cabinTilesCheapToPremium(pricesByType) {
  const entries = Object.entries(pricesByType || {});
  const num = (value) => {
    // CP-268: a cabin with no Current (null) sorts after one with a fare.
    if (value == null || value === '') return Infinity;
    const n = Number(value);
    return Number.isFinite(n) ? n : Infinity;
  };
  const tierOf = (name) => {
    const tier = cabinTier(name);
    return tier == null ? Infinity : tier;
  };
  return entries.sort(([nameA, a], [nameB, b]) => (
    (num(a && a.lowest) - num(b && b.lowest))
    || (num(a && a.current) - num(b && b.current))
    || (tierOf(nameA) - tierOf(nameB))
    || String(nameA).localeCompare(String(nameB))
  ));
}
